import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import { faPencil, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, Modal } from '@shared/components/bootstrap';

import { CategorySearchInput } from '@admin/components/categories/category_search_input';
import { HeroImagePicker } from '@admin/components/item_groups/hero_image_picker';
import { ItemsTable } from '@admin/components/items/items_table';
import { client } from '@admin/libraries/apollo';
import { useItemGroupCreateMutation, useItemGroupUpdateMutation, useItemsBulkUpdateMutation } from '@admin/schema';
import { IImage } from '@admin/types/image';
import { IItem } from '@admin/types/item';

interface CategoryOption {
  name: string;
  category: {
    id: string;
    name: string;
    custom?: boolean;
    defaults?: Record<string, { length?: number; width?: number; height?: number; num_movers?: number }>;
  };
  sizingSelection?: string | null;
}

/**
 * Modal for categorizing items, supporting two modes:
 * - 'item_group': Creates or updates item groups with hero images and packaging
 * - 'categorize': Bulk updates items directly without creating item groups
 */
interface ItemCategorizationModalProps {
  // Common props
  items: IItem[];
  accountID: string; // Required for all operations, should always be available from route
  onClose: () => void;
  onSave: () => void;

  // Mode and item group specific props
  mode?: 'item_group' | 'categorize';
  itemGroupID?: string | null;
  initialCategory?: {
    id: string | number;
    name: string;
  } | null;
  initialSizingSelection?: string | null;
  initialCustomCategoryName?: string | null;
  initialLength?: number | null;
  initialWidth?: number | null;
  initialHeight?: number | null;
  initialHeroImage?: IImage | null;
  initialDimensionsOverwritten?: boolean;
}

export const ItemCategorizationModal: React.FC<ItemCategorizationModalProps> = ({
  items,
  accountID,
  onClose,
  onSave,
  mode = 'item_group',
  itemGroupID,
  initialCategory,
  initialSizingSelection,
  initialCustomCategoryName,
  initialLength,
  initialWidth,
  initialHeight,
  initialHeroImage,
  initialDimensionsOverwritten = false,
}) => {
  const [category, setCategory] = useState<CategoryOption | null>(null);
  const [sizingSelection, setSizingSelection] = useState<string | null>(initialSizingSelection || null);
  const [customCategoryName, setCustomCategoryName] = useState<string | null>(initialCustomCategoryName || null);
  const [length, setLength] = useState<number | null>(initialLength || null);
  const [width, setWidth] = useState<number | null>(initialWidth || null);
  const [height, setHeight] = useState<number | null>(initialHeight || null);
  const [heroImage, setHeroImage] = useState<IImage | null>(initialHeroImage || null);
  const [dimensionsOverwritten, setDimensionsOverwritten] = useState<boolean>(initialDimensionsOverwritten);
  const [updatedItems, setUpdatedItems] = useState<IItem[]>(items);
  const formStateRef = useRef<{ customCategoryName?: string | null; custom_category_name?: string | null }>({});

  // GraphQL mutation hooks
  const [itemGroupCreate, { loading: creatingGroup }] = useItemGroupCreateMutation({ client });
  const [itemGroupUpdate, { loading: updatingGroup }] = useItemGroupUpdateMutation({ client });
  const [itemsBulkUpdate, { loading: bulkUpdating }] = useItemsBulkUpdateMutation({ client });

  const saving = creatingGroup || updatingGroup || bulkUpdating;

  // Initialize category from initialCategory if provided
  useEffect(() => {
    if (initialCategory && !category) {
      setCategory({
        name: initialCategory.name,
        category: {
          id: String(initialCategory.id),
          name: initialCategory.name,
        },
        sizingSelection: initialSizingSelection ?? null,
      });
    }
  }, [initialCategory, category, initialSizingSelection]);

  const findItemCategoryDefaults = useCallback((itemCategory: CategoryOption['category'], sizingSel: string | null) => {
    if (!itemCategory?.defaults) return null;
    return itemCategory.defaults[sizingSel ?? 'default'] ?? null;
  }, []);

  // Helper to clear all dimensions
  const clearDimensions = useCallback(() => {
    setLength(null);
    setWidth(null);
    setHeight(null);
  }, []);

  // Helper to set dimensions from defaults
  const setDimensionsFromDefaults = useCallback(
    (defaults: { length?: number; width?: number; height?: number; num_movers?: number } | null) => {
      if (defaults) {
        setLength(defaults.length ?? null);
        setWidth(defaults.width ?? null);
        setHeight(defaults.height ?? null);
        setDimensionsOverwritten(false);
      } else {
        clearDimensions();
      }
    },
    [clearDimensions],
  );

  // Extract custom category name from CategorySearchInput label
  const extractCustomCategoryName = useCallback((label: string, isCustomCategory: boolean): string | null => {
    if (!isCustomCategory) return null;

    // Try to extract from label format "Custom (name)"
    const customMatch = label.match(/^Custom \((.+)\)$/);
    if (customMatch) {
      return customMatch[1];
    }

    // When virtual category is selected, label is just the custom name (not formatted)
    if (label && label !== 'Custom' && !label.toLowerCase().includes('click here')) {
      return label;
    }

    // Fallback to formStateRef which CategorySearchInput mutates directly
    return formStateRef.current.customCategoryName ?? formStateRef.current.custom_category_name ?? null;
  }, []);

  const handleCategorySelect = useCallback(
    (params: { option: CategoryOption | null; model: CategoryOption | null; label: string }) => {
      const model = params.model ?? params.option;
      if (!model) {
        clearDimensions();
        setCategory(null);
        setCustomCategoryName(null);
        return;
      }

      setCategory(model);
      setSizingSelection(model.sizingSelection ?? null);

      // Extract and set custom category name only if category is custom, otherwise clear it
      if (model.category.custom) {
        const extractedCustomName = extractCustomCategoryName(params.label, true);
        setCustomCategoryName(extractedCustomName);
      } else {
        setCustomCategoryName(null);
      }

      // Set dimensions from category defaults
      const defaults = findItemCategoryDefaults(model.category, model.sizingSelection ?? null);
      setDimensionsFromDefaults(defaults);
    },
    [findItemCategoryDefaults, clearDimensions, setDimensionsFromDefaults, extractCustomCategoryName],
  );

  const handleRevertDimensions = useCallback(() => {
    if (!category) return;
    const defaults = findItemCategoryDefaults(category.category, sizingSelection);
    if (defaults) {
      setLength(defaults.length ?? null);
      setWidth(defaults.width ?? null);
      setHeight(defaults.height ?? null);
    } else {
      setLength(null);
      setWidth(null);
      setHeight(null);
    }
    setDimensionsOverwritten(false);
  }, [category, sizingSelection, findItemCategoryDefaults]);

  const handleOverrideDimensions = useCallback(() => {
    if (category) {
      setDimensionsOverwritten(true);
    }
  }, [category]);

  const handleItemChange = useCallback((updatedItem: IItem) => {
    setUpdatedItems((prev) => prev.map((item) => (item.id === updatedItem.id ? updatedItem : item)));
  }, []);

  const savable = useMemo(() => {
    if (mode === 'item_group') {
      // For item groups, require category, dimensions, and hero image
      return !!(category?.category && length && width && height && heroImage);
    } else {
      // For categorize, require category and dimensions
      return !!(category?.category && length && width && height);
    }
  }, [mode, category, length, width, height, heroImage]);

  const handleSave = useCallback(async () => {
    if (!savable || !category) return;

    try {
      if (mode === 'item_group') {
        // Create or update item group using GraphQL
        // Explicitly set customCategoryName to null if category is not custom
        const customCategoryNameValue = category.category.custom ? (customCategoryName ?? undefined) : null;

        const commonInput = {
          categoryID: category.category.id,
          sizingSelection: sizingSelection ?? undefined,
          customCategoryName: customCategoryNameValue,
          heroImageID: heroImage?.id ? String(heroImage.id) : undefined,
          length: length ?? undefined,
          width: width ?? undefined,
          height: height ?? undefined,
          dimensionsOverwritten: dimensionsOverwritten,
          itemIDs: updatedItems.map((item) => String(item.id)),
          itemsAttributes: updatedItems.map((item) => ({
            id: String(item.id),
            packaging: item.packaging?.type ?? undefined,
            materialCategoryID: item.packaging?.materialCategoryId
              ? String(item.packaging.materialCategoryId)
              : undefined,
            partCategoryID: item.packaging?.partCategoryId ? String(item.packaging.partCategoryId) : undefined,
          })),
        };

        if (itemGroupID) {
          const result = await itemGroupUpdate({
            variables: {
              input: {
                ...commonInput,
                itemGroupID,
              },
            },
          });
          const response = result.data?.itemGroupUpdate;

          if (!response || response.status !== 'OK') {
            throw new Error(response?.error ?? 'Failed to update item group');
          }
        } else {
          const result = await itemGroupCreate({
            variables: {
              input: {
                ...commonInput,
                accountID,
              },
            },
          });
          const response = result.data?.itemGroupCreate;

          if (!response || response.status !== 'OK') {
            throw new Error(response?.error ?? 'Failed to create item group');
          }
        }

        onSave();
      } else {
        // Bulk update items using GraphQL
        // Explicitly set customCategoryName to null if category is not custom
        const customCategoryNameValue = category.category.custom ? (customCategoryName ?? undefined) : null;
        const input = {
          itemIDs: items.map((item) => String(item.id)),
          categoryID: category.category.id,
          sizingSelection: sizingSelection ?? undefined,
          customCategoryName: customCategoryNameValue,
          ...(dimensionsOverwritten
            ? {
                length: length ?? undefined,
                width: width ?? undefined,
                height: height ?? undefined,
                dimensionsOverwritten: true,
              }
            : {}),
        };

        const result = await itemsBulkUpdate({ variables: { input } });

        if (!result.data?.itemsBulkUpdate || result.data.itemsBulkUpdate.status !== 'OK') {
          throw new Error(result.data?.itemsBulkUpdate?.error || 'Failed to update items');
        }

        onSave();
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert(error instanceof Error ? error.message : 'An error occurred while saving');
    }
  }, [
    savable,
    mode,
    category,
    sizingSelection,
    customCategoryName,
    length,
    width,
    height,
    dimensionsOverwritten,
    heroImage,
    itemGroupID,
    accountID,
    updatedItems,
    items,
    itemGroupCreate,
    itemGroupUpdate,
    itemsBulkUpdate,
    onSave,
  ]);

  const title = useMemo(() => {
    if (mode === 'item_group') {
      return itemGroupID
        ? `Item Group #${itemGroupID}`
        : 'Are you sure you want to group these items? Only tech can undo this.';
    }
    return 'Select the category for the items below.';
  }, [mode, itemGroupID]);

  const saveButtonText = useMemo(() => {
    if (mode === 'item_group') {
      return itemGroupID ? 'Yes, Update Item Group' : 'Yes, Create Item Group';
    }
    return `Update ${items.length === 1 ? '1 item' : `${items.length} items`}`;
  }, [mode, itemGroupID, items.length]);

  const formState = useMemo(() => {
    const state = {
      category: category?.category || initialCategory,
      sizing_selection: sizingSelection,
      sizingSelection: sizingSelection,
      custom_category_name: customCategoryName,
      customCategoryName: customCategoryName,
    };
    // Store ref so we can read mutations from CategorySearchInput
    formStateRef.current = state;
    return state;
  }, [category, sizingSelection, customCategoryName, initialCategory]);

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <span className="text-center" style={{ display: 'block', width: '100%' }}>
              {title}
            </span>
          </Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          <ItemsTable
            items={updatedItems}
            mode={mode === 'item_group' ? 'package' : undefined}
            onItemChange={mode === 'item_group' ? handleItemChange : undefined}
          />

          {mode === 'item_group' && (
            <div className="form-group">
              <select className="form-control" value="attached" disabled>
                <option value="attached">
                  Attached: Attached items were once part of the same item that was disassembled.
                </option>
              </select>
            </div>
          )}

          <div className="form-group">
            <CategorySearchInput item={formState} onSelectCallback={handleCategorySelect} />
          </div>

          <div className="row">
            <div className="col-xs-3">
              <div className="form-group">
                <label>Length (in):</label>
                <input
                  name="item_group[length]"
                  type="number"
                  className="form-control"
                  placeholder="Length (in)"
                  value={length || ''}
                  onChange={(e) => setLength(e.target.value ? Number(e.target.value) : null)}
                  disabled={!category || !dimensionsOverwritten}
                />
              </div>
            </div>
            <div className="col-xs-3">
              <div className="form-group">
                <label>Width (in):</label>
                <input
                  name="item_group[width]"
                  type="number"
                  className="form-control"
                  placeholder="Width (in)"
                  value={width || ''}
                  onChange={(e) => setWidth(e.target.value ? Number(e.target.value) : null)}
                  disabled={!category || !dimensionsOverwritten}
                />
              </div>
            </div>
            <div className="col-xs-3">
              <div className="form-group">
                <label>Height (in):</label>
                <input
                  name="item_group[height]"
                  type="number"
                  className="form-control"
                  placeholder="Height (in)"
                  value={height || ''}
                  onChange={(e) => setHeight(e.target.value ? Number(e.target.value) : null)}
                  disabled={!category || !dimensionsOverwritten}
                />
              </div>
            </div>
            <div className="col-xs-3">
              <span className="pull-right">
                {dimensionsOverwritten ? (
                  <Button type="button" kind="default" disabled={!category} onClick={handleRevertDimensions}>
                    <FontAwesomeIcon icon={faRefresh} /> Revert
                  </Button>
                ) : (
                  <Button type="button" kind="default" disabled={!category} onClick={handleOverrideDimensions}>
                    <FontAwesomeIcon icon={faPencil} /> Override
                  </Button>
                )}
              </span>
            </div>
          </div>

          {mode === 'item_group' && (
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 500 }}>Select a Hero Image:</label>
              <HeroImagePicker
                items={updatedItems}
                selection={heroImage || undefined}
                onSelectionChange={setHeroImage}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-default" onClick={onClose} disabled={saving}>
            No, Nevermind
          </button>
          <button type="button" className="btn btn-danger" disabled={!savable || saving} onClick={handleSave}>
            {saving ? 'Saving...' : saveButtonText}
          </button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
